import {
  API_URL,
  CLOUDFLARE_IMAGES_DELIVERY_BASE,
  LOCAL_STORAGE_USER_INFO_FIELD,
} from '../config';
import { dataURItoBlob } from './fileUtils';

function getStoredAuthToken(explicitToken) {
  if (explicitToken) return explicitToken;
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(LOCAL_STORAGE_USER_INFO_FIELD) || '{}';
    const parsed = JSON.parse(raw);
    return parsed.token || null;
  } catch {
    return null;
  }
}

function deliveryBase() {
  return String(CLOUDFLARE_IMAGES_DELIVERY_BASE || '').replace(/\/$/, '');
}

function buildImageUrls(imageId) {
  const base = deliveryBase();
  return {
    thumbnail: `${base}/${imageId}/112x`,
    original: `${base}/${imageId}/public`,
  };
}

async function requestDirectUploadUrl(token, { contentType, appointmentId } = {}) {
  const body = {
    contentType: contentType || 'image/jpeg',
    ...(appointmentId != null && appointmentId !== ''
      ? { appointmentId }
      : {}),
  };

  const urlResponse = await fetch(`${API_URL}/v1/uploads/direct-upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const json = await urlResponse.json().catch(() => ({}));
  if (!urlResponse.ok) {
    const msg =
      json?.message ||
      json?.error ||
      `Could not start upload (${urlResponse.status})`;
    throw new Error(msg);
  }

  if (!json.data?.uploadUrl) {
    throw new Error('Invalid upload URL response from server');
  }

  return json.data;
}

async function postFileToCloudflareUploadUrl(uploadUrl, blob, filename = 'upload.jpg') {
  const formData = new FormData();
  formData.append('file', blob, filename);

  const uploadResponse = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!uploadResponse.ok) {
    throw new Error('Image upload to storage failed');
  }

  const result = await uploadResponse.json().catch(() => ({}));
  const imageId = result?.result?.id;
  if (!imageId) {
    throw new Error('Upload did not return an image id');
  }

  return buildImageUrls(imageId);
}

/**
 * Upload a File/Blob via Cloudflare Images direct upload; returns public + thumbnail URLs.
 */
export async function uploadBlobThroughDirectUpload(blob, token, options = {}) {
  const auth = getStoredAuthToken(token);
  if (!auth) {
    throw new Error('You must be signed in to upload images.');
  }

  const contentType =
    options.contentType || blob.type || 'image/jpeg';
  const filename =
    blob instanceof File && blob.name
      ? blob.name
      : options.filename || 'upload.jpg';

  const { uploadUrl } = await requestDirectUploadUrl(auth, {
    contentType,
    appointmentId: options.appointmentId,
  });

  const fileForForm =
    blob instanceof File
      ? blob
      : new File([blob], filename, { type: contentType });

  return postFileToCloudflareUploadUrl(uploadUrl, fileForForm, filename);
}

/**
 * @returns {Promise<string>} Original (public) image URL for APIs that expect a single string.
 */
export async function uploadFileToCloudflareUrl(file, explicitToken, options = {}) {
  const urls = await uploadBlobThroughDirectUpload(file, explicitToken, {
    ...options,
    contentType: file.type || options.contentType || 'image/jpeg',
    filename: file.name || 'upload.jpg',
  });
  return urls.original;
}

/**
 * @returns {Promise<string>} Original image URL
 */
export async function uploadDataUriToCloudflareUrl(dataUri, explicitToken, options = {}) {
  const blob = dataURItoBlob(dataUri);
  const token = getStoredAuthToken(explicitToken);
  if (!token) {
    throw new Error('You must be signed in to upload images.');
  }
  const file = new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' });
  return uploadFileToCloudflareUrl(file, token, options);
}

/** Chat and other callers: returns thumbnail + original URLs. */
export async function uploadToCloudflare(file, token) {
  const urls = await uploadBlobThroughDirectUpload(file, token, {
    contentType: file.type || 'image/jpeg',
    filename: file.name || 'upload.jpg',
  });
  return {
    thumbnail: urls.thumbnail,
    original: urls.original,
  };
}
